/**
 * Spec §4 product detail content for NextOnVera, Cancer Navigator, and Grace AI.
 * Public-level "what it does / why it matters" only — no architecture, models, or infra detail.
 */

export type ProductAccent = 'cyan' | 'coral' | 'violet';

export type ProductCta = {
  label: string;
  href: string;
  variant: 'cta' | 'default' | 'outline';
};

export type ProductStep = {
  number: string;
  title: string;
  body: string;
};

export type ProductPageContent = {
  id: 'nextonvera' | 'cancer-navigator' | 'grace-ai';
  documentTitle: string;
  indexLabel: string;
  name: string;
  eyebrow: string;
  tagline: string;
  pitch: string;
  status: string;
  statusTone: 'live' | 'pilot' | 'dev';
  accent: ProductAccent;
  problemHeading: string;
  problemBody: string[];
  stepsHeading: string;
  stepsIntro?: string;
  steps: ProductStep[];
  /** Optional naming / rename note (NextOnVera). */
  namingNote?: {
    heading: string;
    body: string;
  };
  /** Optional proof / credibility block. */
  proof?: {
    heading: string;
    body: string;
  };
  /** Optional status / development callout (Cancer Navigator). */
  statusDetail?: {
    heading: string;
    body: string;
  };
  /** Optional clinical guardrail (Grace AI). */
  guardrail?: {
    heading: string;
    body: string;
  };
  /** Show illustrative Grace mockup panels (no real patient data). */
  showGraceMockups?: boolean;
  partners?: string[];
  cta: {
    eyebrow: string;
    heading: string;
    body: string;
    primary: ProductCta;
    secondary: ProductCta;
  };
  /** Optional hero image (public path under BASE_URL). */
  heroImage?: string;
  heroImageAlt?: string;
  /** `cover` fills the text-height frame; `contain` shows the full asset. */
  heroImageFit?: 'cover' | 'contain';
  /** Renders the hero image inside a bevelled 3D metal-cast plate. */
  heroFrame?: 'platinum' | 'gold';
  backLabel: string;
  backHref: string;
};

export const nextonveraProduct: ProductPageContent = {
  id: 'nextonvera',
  documentTitle: 'NextOnVera · NextOnVera',
  indexLabel: 'Project 01',
  name: 'NextOnVera',
  eyebrow: 'Oncology intelligence',
  tagline: 'Metro-level oncology intelligence — designed to reach regional teams.',
  pitch:
    "AI-powered clinical intelligence for oncologists, tumour boards and researchers. NextOnVera builds a structured profile from a patient's full molecular and clinical picture, then surfaces treatments, repurposing options and trials that time-poor teams would otherwise never find — as evidence-based reports the board can work with, never a black-box replacement for them.",
  status: 'Test',
  statusTone: 'pilot',
  heroImage: 'generated-images/nextonvera-hero.png',
  accent: 'cyan',
  problemHeading: 'The problem it solves',
  problemBody: [
    "A patient's story is scattered — genomic sequencing, biomarkers, pathology, imaging and treatment history — and the literature that might match it sits in thousands of disconnected sources. A generalist or small regional team cannot realistically search all of it before the next care decision.",
    'Glioblastoma and brain cancer are the proving ground: complex, fragmented and time-critical. The architecture is pan-cancer, built to extend beyond that once the method is proven.'
  ],
  stepsHeading: 'How it works',
  stepsIntro: 'Read the person. Match the evidence. Put a structured report in front of the people who decide.',
  steps: [
    {
      number: '01',
      title: 'Read',
      body:
        "NextOnVera ingests the full molecular and clinical picture into a single structured profile — genomics, biomarkers, pathology, imaging and treatment history."
    },
    {
      number: '02',
      title: 'Match',
      body:
        'AI and knowledge-graph mapping across drugs, targets, pathways and patient characteristics surface treatment, repurposing and trial options a manual review would miss.'
    },
    {
      number: '03',
      title: 'Verify',
      body:
        'Every recommendation is graded against trusted evidence frameworks, and every citation must resolve to a real, checkable source before a clinician sees it. The output is a board-ready report — not automated care.'
    }
  ],
  namingNote: {
    heading: 'About the name',
    body:
      'NextOnVera was formerly known as NextCure AIx. The name changed for two reasons: “Cure” implied a guaranteed clinical outcome, which isn’t permitted for a clinical decision-support tool under Australian medical device regulation — and “AIx” conflicted with an existing registered trademark. NextOnVera combines onkos (the Greek root of “oncology”) with verus (Latin for “true” / “verified”) — the truth in oncology, verified.'
  },
  proof: {
    heading: 'Early proof, handled carefully',
    body:
      'In its first proof-of-concept phase, NextOnVera identified a biomarker classification discrepancy in a real patient record that would have affected treatment decisions — subsequently confirmed accurate on clinical review. It has also matched real patients to specific, currently-recruiting clinical trials based on their individual tumour genomics.'
  },
  statusDetail: {
    heading: 'Where it stands',
    body:
      'Phase 0 is complete. NextOnVera is piloting with Monash Health, Monash University and The Alfred Hospital, with a live brain-cancer case currently under oncology review at The Alfred. Built cancer-agnostic — glioblastoma is the proving ground, with early validation also shown for breast and lung cancer matching.'
  },
  partners: ['Monash Health', 'Monash University', 'The Alfred Hospital', 'Tour De Cure'],
  cta: {
    eyebrow: 'Fund the work',
    heading: 'Help scale intelligence that travels beyond metro centres.',
    body:
      'NextOnVera closes the gap between a tumour’s full clinical picture and options already sitting in the literature. Support the fund so more teams — not only those near major hospitals — can use it.',
    primary: { label: 'Support the Fund', href: '/the-fund', variant: 'cta' },
    secondary: { label: 'Get involved', href: '/get-involved', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const cancerNavigatorProduct: ProductPageContent = {
  id: 'cancer-navigator',
  documentTitle: 'Cancer Navigator · NextOnVera',
  indexLabel: 'Project 03',
  name: 'Cancer Navigator',
  eyebrow: 'Orientation after diagnosis',
  tagline: 'A guided starting point when diagnosis turns the world upside down.',
  pitch:
    'A patient-and-carer experience, shaped with consumer advocates, for trusted guidance and practical next steps when life has just been rearranged. The third piece of the journey alongside NextOnVera and Grace AI.',
  status: 'In development',
  statusTone: 'dev',
  heroImage: 'generated-images/cancer-navigator-hero.png',
  heroImageAlt:
    'Cancer Navigator preview: a Tour De Cure initiative with search, diagnosis, carer, and learning starting points.',
  heroImageFit: 'contain',
  heroFrame: 'platinum',
  accent: 'violet',
  problemHeading: 'The problem it fills',
  problemBody: [
    '“It fills a gap I know personally to be significant and largely unaddressed for patients and families trying to navigate a diagnosis.” — Mark Norris',
    'A brain cancer diagnosis drops a patient and their family into an overwhelming, fragmented system overnight — different specialists, unfamiliar terminology, treatment options, financial and practical questions, and no single trusted place to start. Cancer Navigator exists to be that starting point.'
  ],
  stepsHeading: 'What we are building toward',
  stepsIntro:
    'Cancer Navigator is earlier-stage than our other funded projects. The public detail stays deliberately modest until the product is ready.',
  steps: [
    {
      number: '01',
      title: 'Orient',
      body:
        'Help newly diagnosed people and their carers find trusted information at the moment of highest confusion — without the noise of an open web search.'
    },
    {
      number: '02',
      title: 'Support both sides of care',
      body:
        'Design for patients and the people who hold them up: tools that answer practical questions families actually face between appointments.'
    },
    {
      number: '03',
      title: 'Shape with advocates',
      body:
        'Build with patient and carer consumer advocates so the portal answers real needs, not assumed ones — and keeps evolving with lived experience.'
    }
  ],
  statusDetail: {
    heading: 'Honest status',
    body:
      'Cancer Navigator is currently in development, being built in partnership with patient and carer consumer advocates. There is no public launch date and no unfinished product screenshots on this site. Register your interest to be notified when it becomes available, or get involved to help shape it.'
  },
  cta: {
    eyebrow: 'Register interest',
    heading: 'Be part of shaping Cancer Navigator.',
    body:
      'If you want early updates when Cancer Navigator is ready — or you bring lived experience that should influence it — we want to hear from you.',
    primary: { label: 'Register interest', href: '/get-involved', variant: 'cta' },
    secondary: { label: 'Support the Fund', href: '/the-fund', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const graceAiProduct: ProductPageContent = {
  id: 'grace-ai',
  documentTitle: 'Grace AI · NextOnVera',
  indexLabel: 'Project 02',
  name: 'Grace AI',
  eyebrow: 'Patient wellness companion',
  tagline: 'Built the opposite way to a chatbot — it asks, it does not answer.',
  pitch:
    'Patients scan a personal QR code and talk about how they feel. Grace turns that conversation into structured, timestamped wellness data — keeping their own words — so trends reach the oncology nurse before the next appointment.',
  status: 'Test',
  statusTone: 'live',
  accent: 'coral',
  problemHeading: 'The problem it solves',
  problemBody: [
    'Between appointments, most of a cancer journey is lived privately — fatigue, sleep, mood, pain — and limited oncology support time is spread evenly instead of toward whoever needs it most right now.',
    'Grace sits in that gap: check-ins that create continuous patient-reported signal, so care teams can move earlier when patterns change, without waiting for the next booked slot.'
  ],
  stepsHeading: 'How it works',
  stepsIntro: 'Talk first. Structure the signal. Hand risk to a person. No diagnosis. No automated advice.',
  steps: [
    {
      number: '01',
      title: 'Check in',
      body:
        'A personal QR opens a conversation. The patient talks about how they are feeling that day — in their own words.'
    },
    {
      number: '02',
      title: 'Understand',
      body:
        'Grace structures the check-in over time, with sentiment and trends. One mention of fatigue is a data point; a worsening pattern across visits is a clinical signal.'
    },
    {
      number: '03',
      title: 'Escalate',
      body:
        'Never diagnoses or advises. Any safety risk stops and hands to a person. Care teams see who needs attention now — including sideways insight across the wider patient cohort.'
    }
  ],
  guardrail: {
    heading: 'Clinical guardrail',
    body:
      'Grace AI does not diagnose, treat, or advise. It listens, structures, and connects patients to their human care team faster. Any response indicating a safety concern is escalated to a person immediately, without exception.'
  },
  statusDetail: {
    heading: 'Where it stands',
    body:
      'Grace AI is live in active companion use. The panels below are de-identified illustratives styled for this site (no real patient names, free text, or identifiers).'
  },
  showGraceMockups: true,
  heroImage: '/generated-images/grace-ai-hero.png',
  heroImageAlt: 'Grace AI patient check-in interface showing conversation prompts and wellness trends',
  heroImageFit: 'contain',
  heroFrame: 'gold',
  cta: {
    eyebrow: 'Fund everyday support',
    heading: 'Keep the companion in patients’ pockets between visits.',
    body:
      'Grace AI turns quiet daily signals into earlier human care. Supporting the Brain Cancer Fund helps expand this live companion for more people navigating treatment.',
    primary: { label: 'Support the Fund', href: '/the-fund', variant: 'cta' },
    secondary: { label: 'Get involved', href: '/get-involved', variant: 'outline' }
  },
  backLabel: 'All projects',
  backHref: '/#what-we-fund'
};

export const productPages = {
  nextonvera: nextonveraProduct,
  'cancer-navigator': cancerNavigatorProduct,
  'grace-ai': graceAiProduct
} as const;
